import { useCallback, useEffect, useRef, useState } from 'react';
import {
  deposerParapheur,
  getParapheur,
  modifierFichierParapheur,
  supprimerFichierParapheur,
  transformerFichierParapheur,
  urlFichierParapheur,
} from '../services/contratService';
import { getContacts } from '../services/contactService';
import { formatTaille } from '../services/documentClientService';
import { formatDate } from './devisUtils';
import ConfirmationSuppression from '../components/ConfirmationSuppression';
import { FlashToast } from '../components/Toasts';
import VignetteFichier from '../components/VignetteFichier';
import NomEditable from './NomEditable';
import '../assets/styles/Devis.css';
import '../assets/styles/DemandeDevis.css';
import '../assets/styles/Drive.css';
import '../assets/styles/Parametrage.css';

const FORMATS_ACCEPTES = '.pdf,.doc,.docx,.odt,.jpg,.jpeg,.png,.webp';

const ETATS = [
  { cle: '', label: 'Tous', compteur: 'tous' },
  { cle: 'a_transformer', label: 'À transformer', compteur: 'a_transformer' },
  { cle: 'transforme', label: 'Transformés', compteur: 'transforme' },
];

/** Pictogramme de repli (Word, formats sans aperçu) : le même que le drive du client. */
function IconeFichier({ mime }) {
  const type = String(mime || '');
  const variante = type === 'application/pdf' ? 'pdf' : type.startsWith('image/') ? 'image' : type.includes('word') ? 'doc' : 'autre';
  const sigle = variante === 'pdf' ? 'PDF' : variante === 'image' ? 'IMG' : variante === 'doc' ? 'DOC' : 'FIC';
  return (
    <svg className={`drive-icone drive-icone-${variante}`} viewBox="0 0 40 48" width="40" height="48" aria-hidden="true">
      <path d="M4 4a3 3 0 0 1 3-3h17l13 13v30a3 3 0 0 1-3 3H7a3 3 0 0 1-3-3V4z" className="drive-icone-page" />
      <path d="M24 1l13 13H27a3 3 0 0 1-3-3V1z" className="drive-icone-corne" />
      <rect x="4" y="26" width="33" height="14" rx="3" className="drive-icone-bandeau" />
      <text x="20.5" y="36.5" textAnchor="middle" className="drive-icone-sigle">
        {sigle}
      </text>
    </svg>
  );
}

/**
 * Transformer un fichier en contrat : il faut le client. On le cherche parmi
 * les demandes (société, nom, email) ; le contrat créé porte l'origine
 * « externe » et le fichier devient son document.
 */
function TransformerModale({ fichier, onClose, onTransforme }) {
  const [recherche, setRecherche] = useState('');
  const [resultats, setResultats] = useState([]);
  const [demande, setDemande] = useState(null);
  const [intitule, setIntitule] = useState('');
  const [enCours, setEnCours] = useState(false);
  const [erreur, setErreur] = useState(null);

  useEffect(() => {
    if (demande) return undefined;
    let vivant = true;
    const t = setTimeout(() => {
      getContacts({ search: recherche.trim(), limit: 8, sortBy: 'date', sortDir: 'DESC' })
        .then((res) => vivant && setResultats(res.data || []))
        .catch(() => vivant && setResultats([]));
    }, 250);
    return () => {
      vivant = false;
      clearTimeout(t);
    };
  }, [recherche, demande]);

  async function valider(e) {
    e.preventDefault();
    if (!demande) {
      setErreur('Choisissez la demande (le client) de ce contrat.');
      return;
    }
    setEnCours(true);
    setErreur(null);
    try {
      const res = await transformerFichierParapheur(fichier.id, { contactId: demande.id, intitule });
      onTransforme(res);
    } catch (err) {
      setErreur(err.message || 'Transformation impossible.');
    } finally {
      setEnCours(false);
    }
  }

  const libelleDemande = (d) =>
    [d.societe_nom, [d.prenom, d.nom].filter(Boolean).join(' ')].filter(Boolean).join(' — ') || `Demande n° ${d.id}`;

  return (
    <div className="param-modal-overlay" onClick={onClose}>
      <div className="param-modal" style={{ maxWidth: 560, height: 'auto' }} onClick={(e) => e.stopPropagation()}>
        <div className="param-modal-header">
          <h2 className="param-modal-title">Transformer en contrat</h2>
          <button type="button" className="param-modal-close" onClick={onClose} aria-label="Fermer">
            ×
          </button>
        </div>
        <form className="param-modal-body drive-formulaire" onSubmit={valider}>
          <p className="drive-vide-texte" style={{ marginTop: 0 }}>
            Le fichier <strong>{fichier.libelle}</strong> devient un contrat de la liste, marqué{' '}
            <strong>externe</strong> (établi hors de l’application). Son fichier est son document : il n’est jamais
            régénéré.
          </p>

          <div className="drive-formulaire-champs">
            <label className="drive-champ">
              <span className="drive-champ-libelle">Demande (client)</span>
              {demande ? (
                <span className="parapheur-demande-choisie">
                  {libelleDemande(demande)}
                  <button type="button" className="drive-action" onClick={() => setDemande(null)}>
                    Changer
                  </button>
                </span>
              ) : (
                <input
                  type="search"
                  className="devis-search"
                  placeholder="Rechercher une société, un nom, un email…"
                  value={recherche}
                  onChange={(e) => setRecherche(e.target.value)}
                  autoFocus
                />
              )}
            </label>

            {!demande && (
              <ul className="parapheur-resultats" aria-label="Demandes trouvées">
                {resultats.length === 0 && <li className="parapheur-resultat-vide">Aucune demande trouvée.</li>}
                {resultats.map((d) => (
                  <li key={d.id}>
                    <button type="button" className="parapheur-resultat" onClick={() => setDemande(d)}>
                      <span>{libelleDemande(d)}</span>
                      <span className="parapheur-resultat-meta">
                        {d.email || ''} {d.date_soumission ? `· ${formatDate(d.date_soumission)}` : ''}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}

            <label className="drive-champ">
              <span className="drive-champ-libelle">Intitulé de l’action (facultatif)</span>
              <input
                type="text"
                className="devis-search"
                value={intitule}
                onChange={(e) => setIntitule(e.target.value)}
                placeholder="Par défaut : le nom du fichier"
                maxLength={500}
              />
            </label>
          </div>

          {erreur && <div className="devis-row-erreur">{erreur}</div>}

          <div className="devis-modal-actions" style={{ marginTop: 16 }}>
            <button type="button" className="devis-btn" onClick={onClose} disabled={enCours}>
              Annuler
            </button>
            <button type="submit" className="devis-btn devis-btn-primary" disabled={enCours || !demande}>
              {enCours ? 'Création…' : 'Créer le contrat'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/**
 * Parapheur : le « drive » des contrats établis hors de l’application.
 *
 * On y dépose plusieurs fichiers d’un coup, on les retrouve en vignettes, et
 * chacun peut être transformé en entité contrat, qui rejoint alors la liste
 * des contrats (origine « externe »). Supprimer un fichier ne l’efface pas du
 * disque : il part à la corbeille.
 */
function Parapheur({ onContratCree, fichiersADeposer, onDepotTraite }) {
  const [fichiers, setFichiers] = useState([]);
  const [compteurs, setCompteurs] = useState({});
  const [etat, setEtat] = useState('');
  const [search, setSearch] = useState('');
  const [recherche, setRecherche] = useState('');
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ total: 0, totalPages: 1 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reload, setReload] = useState(0);
  const [flash, setFlash] = useState(null);
  const [depotEnCours, setDepotEnCours] = useState(false);
  const [survol, setSurvol] = useState(false);
  const [transformation, setTransformation] = useState(null);
  const [renommageId, setRenommageId] = useState(null);
  const [suppression, setSuppression] = useState(null);
  const [suppressionEnCours, setSuppressionEnCours] = useState(false);
  const selecteur = useRef(null);

  useEffect(() => {
    const t = setTimeout(() => {
      setRecherche(search);
      setPage(1);
    }, 300);
    return () => clearTimeout(t);
  }, [search]);

  const charger = useCallback(() => setReload((n) => n + 1), []);

  useEffect(() => {
    let vivant = true;
    setLoading(true);
    setError(null);
    getParapheur({ search: recherche, etat, page, limit: 24 })
      .then((res) => {
        if (!vivant) return;
        setFichiers(res.data || []);
        setCompteurs(res.compteurs || {});
        setPagination(res.pagination || { total: 0, totalPages: 1 });
      })
      .catch((err) => vivant && setError(err.message || 'Impossible de charger le Parapheur.'))
      .finally(() => vivant && setLoading(false));
    return () => {
      vivant = false;
    };
  }, [recherche, etat, page, reload]);

  /** Dépose les fichiers choisis (plusieurs à la fois) ; chacun est traité à part. */
  const deposer = useCallback(
    async (liste) => {
      const fichiersChoisis = Array.from(liste || []);
      if (!fichiersChoisis.length) return;
      setDepotEnCours(true);
      setError(null);
      try {
        const res = await deposerParapheur(fichiersChoisis);
        const { deposes = [], refuses = [] } = res.data || {};
        setFlash(
          `${deposes.length} fichier${deposes.length > 1 ? 's' : ''} déposé${deposes.length > 1 ? 's' : ''} dans le Parapheur` +
            (refuses.length ? ` · ${refuses.length} refusé${refuses.length > 1 ? 's' : ''} (${refuses.map((r) => `${r.nom} : ${r.raison}`).join(' ; ')})` : '')
        );
        setEtat('');
        setPage(1);
        charger();
      } catch (err) {
        setError(err.message || 'Dépôt impossible.');
      } finally {
        setDepotEnCours(false);
      }
    },
    [charger]
  );

  // Fichiers choisis depuis la liste des contrats (« Importer ») : déposés ici.
  useEffect(() => {
    if (fichiersADeposer && fichiersADeposer.length) {
      deposer(fichiersADeposer);
      onDepotTraite?.();
    }
  }, [fichiersADeposer, deposer, onDepotTraite]);

  async function renommer(fichier, nom) {
    await modifierFichierParapheur(fichier.id, { libelle: nom });
    charger();
  }

  async function supprimer() {
    if (!suppression) return;
    setSuppressionEnCours(true);
    try {
      await supprimerFichierParapheur(suppression.id);
      setFlash(`« ${suppression.libelle} » retiré du Parapheur (corbeille).`);
      setSuppression(null);
      charger();
    } catch (err) {
      setError(err.message || 'Suppression impossible.');
      setSuppression(null);
    } finally {
      setSuppressionEnCours(false);
    }
  }

  return (
    <div
      className={'devis-page parapheur' + (survol ? ' parapheur-survol' : '')}
      onDragOver={(e) => {
        e.preventDefault();
        setSurvol(true);
      }}
      onDragLeave={(e) => {
        if (e.currentTarget === e.target) setSurvol(false);
      }}
      onDrop={(e) => {
        e.preventDefault();
        setSurvol(false);
        deposer(e.dataTransfer.files);
      }}
    >
      <div className="devis-header">
        <div>
          <h1 className="devis-title">Parapheur</h1>
          <p className="drive-vide-texte" style={{ margin: '4px 0 0' }}>
            Les contrats établis hors de l’application : déposez-les ici (plusieurs à la fois), puis transformez chacun
            en contrat.
          </p>
        </div>
        <div className="devis-header-actions">
          <button
            type="button"
            className="devis-btn devis-btn-primary"
            onClick={() => selecteur.current?.click()}
            disabled={depotEnCours}
          >
            {depotEnCours ? 'Dépôt en cours…' : 'Importer des fichiers'}
          </button>
          <input
            ref={selecteur}
            type="file"
            className="drive-input-cache"
            accept={FORMATS_ACCEPTES}
            multiple
            onChange={(e) => {
              deposer(e.target.files);
              e.target.value = '';
            }}
            aria-label="Choisir des fichiers à déposer dans le Parapheur"
          />
        </div>
      </div>

      <div className="devis-toolbar">
        <input
          type="text"
          className="devis-search"
          placeholder="Rechercher un fichier, un client, un contrat…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <div className="parapheur-etats" role="group" aria-label="Filtrer les fichiers">
          {ETATS.map((o) => (
            <button
              key={o.cle || 'tous'}
              type="button"
              className={'parapheur-etat' + (etat === o.cle ? ' parapheur-etat-actif' : '')}
              onClick={() => {
                setEtat(o.cle);
                setPage(1);
              }}
            >
              {o.label} <span className="parapheur-etat-compte">{compteurs[o.compteur] ?? 0}</span>
            </button>
          ))}
        </div>
      </div>

      {error && <div className="devis-state devis-state-error">{error}</div>}
      {loading && <div className="devis-state">Chargement du Parapheur…</div>}

      {!loading && !error && fichiers.length === 0 && (
        <div className="drive-vide">
          <div className="drive-vide-titre">
            {etat || recherche ? 'Aucun fichier ne correspond à cette vue.' : 'Le Parapheur est vide.'}
          </div>
          <div className="drive-vide-texte">
            Glissez des contrats (PDF, Word, images) n’importe où sur cette page, ou utilisez « Importer des fichiers ».
          </div>
        </div>
      )}

      {!loading && fichiers.length > 0 && (
        <div className="drive-grille parapheur-grille">
          {fichiers.map((f) => (
            <article className="drive-tuile" key={f.id}>
              <div
                className="drive-tuile-visuel drive-tuile-visuel-apercu"
                role="button"
                tabIndex={0}
                title="Ouvrir"
                onClick={() => window.open(urlFichierParapheur(f.id, { apercu: true }), '_blank')}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    window.open(urlFichierParapheur(f.id, { apercu: true }), '_blank');
                  }
                }}
              >
                <VignetteFichier
                  url={urlFichierParapheur(f.id, { apercu: true })}
                  mime={f.mime}
                  alt={`Aperçu de ${f.libelle}`}
                  secours={<IconeFichier mime={f.mime} />}
                  largeur={232}
                />
              </div>

              <div className="drive-tuile-corps">
                <div className="drive-tuile-nom" title={f.libelle}>
                  <NomEditable
                    valeur={f.libelle}
                    edition={renommageId === f.id}
                    onRenommer={(nom) => renommer(f, nom)}
                    onFermer={() => setRenommageId(null)}
                  />
                </div>
                <div className="drive-tuile-meta" title={f.nomFichier}>
                  {f.nomFichier}
                </div>
                <div className="drive-tuile-meta">
                  {formatTaille(f.taille)} · {formatDate(f.creeLe)}
                  {f.deposePar && <span className="drive-origine">{f.deposePar}</span>}
                </div>
                {f.clientNom && <div className="drive-tuile-meta">{f.clientNom}</div>}
                {f.transforme ? (
                  <div className="drive-tuile-meta">
                    <span className="badge-origine badge-origine-externe" style={{ marginLeft: 0 }}>
                      Contrat {f.contratNumero}
                    </span>
                  </div>
                ) : (
                  f.contratSupprime && (
                    <div className="drive-tuile-meta">Contrat {f.contratNumero} à la corbeille</div>
                  )
                )}
              </div>

              <div className="drive-tuile-actions">
                {!f.transforme && (
                  <button
                    type="button"
                    className="drive-action drive-action-forte"
                    onClick={() => setTransformation(f)}
                    title="Créer un contrat (origine externe) à partir de ce fichier"
                  >
                    Transformer en contrat
                  </button>
                )}
                <button
                  type="button"
                  className="drive-action"
                  onClick={() => window.open(urlFichierParapheur(f.id, { apercu: true }), '_blank')}
                >
                  Ouvrir
                </button>
                <a className="drive-action" href={urlFichierParapheur(f.id)} download title="Télécharger">
                  Télécharger
                </a>
                <button type="button" className="drive-action" onClick={() => setRenommageId(f.id)} title="Renommer">
                  Renommer
                </button>
                <button
                  type="button"
                  className="drive-action drive-action-danger"
                  onClick={() => setSuppression(f)}
                  title="Retirer du Parapheur (corbeille ; le fichier reste sur le disque)"
                >
                  Retirer
                </button>
              </div>
            </article>
          ))}
        </div>
      )}

      {!loading && pagination.totalPages > 1 && (
        <div className="devis-pagination">
          <span className="devis-pagination-info">
            Page {page} sur {pagination.totalPages} · {pagination.total} fichier{pagination.total > 1 ? 's' : ''}
          </span>
          <div className="devis-pagination-controls">
            <button type="button" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
              Précédent
            </button>
            <button type="button" disabled={page >= pagination.totalPages} onClick={() => setPage((p) => p + 1)}>
              Suivant
            </button>
          </div>
        </div>
      )}

      {transformation && (
        <TransformerModale
          fichier={transformation}
          onClose={() => setTransformation(null)}
          onTransforme={(res) => {
            setTransformation(null);
            setFlash(res.message || 'Contrat créé.');
            charger();
            onContratCree?.(res.data);
          }}
        />
      )}

      {suppression && (
        <ConfirmationSuppression
          titre="Retirer ce fichier du Parapheur ?"
          sousTitre={suppression.libelle}
          details={['Le fichier, retiré du Parapheur.']}
          note="Le fichier reste sur le disque ; il part à la corbeille et peut être restauré. Un contrat déjà créé depuis ce fichier n’est pas touché."
          libelleConfirmer="Oui, retirer"
          enCours={suppressionEnCours}
          onConfirmer={supprimer}
          onAnnuler={() => setSuppression(null)}
        />
      )}

      <FlashToast texte={flash} onFermer={() => setFlash(null)} />
    </div>
  );
}

export default Parapheur;
